// Users — fleet identities that have created sandboxes and/or driven MCP tool
// calls. Read-only correlation from GET /api/v1/fleet-users (admin-gated on the
// server; a viewer who reaches this URL gets 403s). Click a row to see that
// user's sandboxes and the MCP servers they've actually used.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Avatar, Badge, Card, Flex, Input, Space, Table, Tag, Tooltip, Typography, theme } from "antd";
import type { BadgeProps, TableColumnsType } from "antd";
import { ChevronDown, ChevronRight, Search, Server, Boxes } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { HintIcon } from "@/components/agate";
import { useAuth } from "@/lib/auth/AuthProvider";
import {
  fleetApi,
  type FleetUser as RegisteredUser,
  type FleetIssue,
  type FleetIssues,
  type IssueKind,
} from "@/lib/api/fleet";
import { displayFullName, displayEmail } from "@/lib/displayName";

const { Text, Title } = Typography;

// Short, honest labels per issue kind (color-blind-safe: the WORD carries the
// meaning, not just the tone — agate DP 3e#8).
const ISSUE_LABEL: Record<IssueKind, string> = {
  "sso-no-sandbox": "No sandbox",
  "sso-no-sandbox-stale": "Never onboarded",
  "issued-not-authorized": "Not authorized",
  "sandbox-idle-no-client": "Idle · no client",
  "host-not-enforcing": "Not enforcing",
  "scan-stale": "Scan stale",
  "registry-degraded": "Registry degraded",
  "host-unreachable": "Unreachable",
};

// Severity ranking so the most serious issue wins when a subject has several.
const SEV_RANK: Record<FleetIssue["severity"], number> = { danger: 0, warning: 1, info: 2 };

function issueTone(i: FleetIssue): "danger" | "warning" | "info" | "muted" {
  if (i.status === "indeterminate") return "muted"; // we're blind, not firing
  return i.severity === "danger" ? "danger" : i.severity === "warning" ? "warning" : "info";
}

// AntD's own Badge status dot — same component + API already used for
// "Enforcing Policy" on the Fleet host card (HostCard.tsx:298). A real
// status dot reads clearer at small sizes than a mono glyph character.
const TONE_BADGE: Record<ReturnType<typeof issueTone>, BadgeProps["status"]> = {
  danger: "error", warning: "warning", info: "processing", muted: "default",
};

// Same tones, mapped onto Alert's own type instead — used for the host/system
// issue summary above the table, where the explanation belongs directly in
// the Alert's description rather than behind a hover-only HintIcon. Alert has
// no "muted"/indeterminate type of its own; "info" is the closest honest fit
// for "we can't confirm this right now."
const TONE_ALERT: Record<ReturnType<typeof issueTone>, "success" | "info" | "warning" | "error"> = {
  danger: "error", warning: "warning", info: "info", muted: "info",
};

function relAge(sec: unknown): string {
  const n = typeof sec === "number" ? sec : NaN;
  if (!Number.isFinite(n)) return "some time ago";
  if (n < 90) return "just now";
  if (n < 5400) return `${Math.round(n / 60)} min ago`;
  if (n < 172800) return `${Math.round(n / 3600)}h ago`;
  return `${Math.round(n / 86400)}d ago`;
}

// Honest, actionable text. Never claims we saw the client error or who connected.
function issueTip(i: FleetIssue): string {
  const ev = i.evidence || {};
  if (i.status === "indeterminate") {
    return `We can't confirm this right now (${String(ev.reason || "a scan or the registry is not current")}). It is neither confirmed nor cleared.`;
  }
  switch (i.kind) {
    case "sso-no-sandbox":
      return `SSO certificate issued ${relAge(ev.age_s)}, no sandbox yet. The failure is on the developer's side and is not visible here — check their public IP is on the SSH :22 / CA :9443 allow-list and that they are on a current client. A certificate being issued confirms the CA signed one, not who connected.`;
    case "sso-no-sandbox-stale":
      return `Access was provisioned but no sandbox has appeared in over 24 hours. Likely never completed onboarding.`;
    case "issued-not-authorized":
      return `A certificate was issued for this principal, but it is not on the sandbox access allow-list (access: ${String(ev.access ?? "revoked")}).`;
    case "sandbox-idle-no-client":
      return `The sandbox is running but no client is attached${ev.uptime ? ` (up ${String(ev.uptime)})` : ""}. Often normal right after creation.`;
    case "host-not-enforcing":
      return `${i.host ?? "This host"} is running but enforcement is not active, so it will not accept sandboxes.`;
    case "scan-stale":
      return `The fleet scan is stale (${String(ev.scan_age_s ?? "?")}s old). Some conditions can't be confirmed or cleared until it refreshes.`;
    case "registry-degraded":
      return `The user registry could not be refreshed (the CA log was unreadable). The list below may be incomplete, so absence of an issue does not mean healthy.`;
    case "host-unreachable":
      return `${i.host ?? "A host"} could not be reached by the last scan.`;
    default:
      return i.kind;
  }
}

// Same facts as issueTip(), condensed to a single line — used for the
// host/system issue Alerts above the table, which carry no separate
// description (see the summary block's own doc comment for why).
// Only ever called on host/system-scoped issues (metaIssues), so the
// user-scoped kinds never reach the default branch in practice.
function issueLine(i: FleetIssue): string {
  const ev = i.evidence || {};
  if (i.status === "indeterminate") {
    return `Can't confirm right now (${String(ev.reason || "a scan or the registry is not current")}) — neither confirmed nor cleared.`;
  }
  switch (i.kind) {
    case "host-not-enforcing":
      return `${i.host ?? "This host"} is running but not enforcing — won't accept sandboxes.`;
    case "scan-stale":
      return `Fleet scan is stale (${String(ev.scan_age_s ?? "?")}s old) — some conditions can't be confirmed.`;
    case "registry-degraded":
      return "User registry couldn't refresh (CA log unreadable) — list may be incomplete.";
    case "host-unreachable":
      return `${i.host ?? "A host"} could not be reached by the last scan.`;
    default:
      return `${ISSUE_LABEL[i.kind]} — ${i.subject.display}`;
  }
}

interface FleetMCPServer {
  server: string;
  tool_count: number;
  last_used: string;
}
interface FleetSandbox {
  id: string;
  username: string;
  state: string;
  public_ip?: string;
  created_at: string;
  last_started_at?: string;
}
interface FleetUser {
  identity: string;
  last_active: string;
  mcp_servers: FleetMCPServer[];
  sandboxes: FleetSandbox[];
  tool_call_count: number;
}

// Go emits a zero time.Time as year 0001 when a user has no activity signal.
function isZeroTime(s: string): boolean {
  return !s || s.startsWith("0001-01-01");
}
function fmtWhen(s: string): string {
  if (isZeroTime(s)) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}
function relDays(s: string): string {
  if (isZeroTime(s)) return "never";
  const ms = Date.now() - new Date(s).getTime();
  const days = Math.floor(ms / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  return `${days}d ago`;
}

const STATE_COLOR: Record<string, string | undefined> = {
  active: "success",
  provisioning: "processing",
  stopped: undefined,
  failed: "error",
  removed: undefined,
};

const ACCESS_COLOR: Record<string, string | undefined> = {
  active: "success",
  revoked: "error",
  unknown: undefined,
};

export default function Users() {
  const { token } = useAuth();
  const { token: antToken } = theme.useToken();
  const [users, setUsers] = useState<FleetUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState("");
  // Fleet users — the fleetd registry (learned from SSO logins + ssh-key grants).
  // Separate source/shape from the MCP/sandbox-activity correlation below.
  const [registeredUsers, setRegisteredUsers] = useState<RegisteredUser[]>([]);
  const [registeredErr, setRegisteredErr] = useState<string | null>(null);
  // Derived sandbox diagnostics (fleetd /api/fleet/issues), polled on the fleet cadence.
  const [issues, setIssues] = useState<FleetIssues | null>(null);

  // Appends request_id (also the X-Request-Id response header) when present —
  // 500 bodies are deliberately generic, so it's the only thing a user can
  // hand support to correlate against.
  const explain = async (res: Response, fallback: string): Promise<string> => {
    try {
      const body = await res.json();
      const message = typeof body.detail === "string" ? body.detail : `${fallback} (${res.status})`;
      return typeof body.request_id === "string" ? `${message} (ref: ${body.request_id})` : message;
    } catch {
      return `${fallback} (${res.status})`;
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/v1/fleet-users`, {
        headers: { Authorization: `Bearer ${token ?? ""}` },
      });
      if (!res.ok) throw new Error(await explain(res, "Could not load users"));
      const body = (await res.json()) as { items: FleetUser[] };
      setUsers(body.items ?? []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    fleetApi.users()
      .then(setRegisteredUsers)
      .catch((e) => setRegisteredErr(e instanceof Error ? e.message : String(e)));
  }, []);

  useEffect(() => {
    let alive = true;
    const load = () => fleetApi.issues().then((r) => { if (alive) setIssues(r); }).catch(() => { /* keep last; a fetch blip isn't a diagnosis */ });
    void load();
    const t = window.setInterval(load, 20000); // align with the fleet page poll
    return () => { alive = false; window.clearInterval(t); };
  }, []);

  // Most-severe issue per user identity (user-scoped kinds only). Host/system issues
  // (host-not-enforcing, scan-stale, registry-degraded, host-unreachable) don't attach
  // to a row — they surface in the summary so they're never lost.
  const issueByUser = useMemo(() => {
    const m = new Map<string, FleetIssue>();
    for (const i of issues?.issues ?? []) {
      if (i.subject.type !== "user") continue;
      const cur = m.get(i.subject.id);
      if (!cur || SEV_RANK[i.severity] < SEV_RANK[cur.severity]) m.set(i.subject.id, i);
    }
    return m;
  }, [issues]);
  const metaIssues = useMemo(
    () => (issues?.issues ?? []).filter((i) => i.subject.type !== "user"),
    [issues],
  );
  const blind = !!issues && (!issues.scan_ok || !issues.registry_ok);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return users;
    return users.filter(
      (u) =>
        u.identity.toLowerCase().includes(needle) ||
        u.mcp_servers.some((s) => s.server.toLowerCase().includes(needle)) ||
        u.sandboxes.some((s) => s.username.toLowerCase().includes(needle)),
    );
  }, [users, q]);

  const registeredColumns: TableColumnsType<RegisteredUser> = [
    {
      // Same Avatar + name/email treatment as the Sandboxes table's Owner
      // column on the Fleet page — one User style across the app.
      title: "User", dataIndex: "display", key: "display",
      render: (_, u) => (
        <Space size={8} align="center">
          <Avatar size="small" style={{ background: antToken.colorPrimary, fontSize: 11 }}>
            {(u.display || u.identity).slice(0, 2).toUpperCase()}
          </Avatar>
          <div style={{ minWidth: 0 }}>
            <span style={{ display: "block" }}>
              <Text strong style={{ fontSize: 13, lineHeight: 1.3 }}>{u.display || u.identity}</Text>
              {u.owner_kind === "agent" && u.associated_human && (
                <Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>· operated by {u.associated_human}</Text>
              )}
            </span>
            <Text
              type="secondary"
              style={{ fontSize: 11, fontFamily: "var(--font-mono)", display: "block" }}
              ellipsis={{ tooltip: u.identity }}
            >
              {u.identity}
            </Text>
          </div>
        </Space>
      ),
    },
    { title: "Type", key: "owner_kind", render: (_, u) => <Tag color={u.owner_kind === "agent" ? "blue" : undefined}>{u.owner_kind}</Tag> },
    { title: "Source", key: "source", render: (_, u) => <Text type="secondary">{u.source === "sso" ? "SSO" : "SSH key"}</Text> },
    { title: "Sandboxes", dataIndex: "sandbox_count", key: "sandbox_count" },
    {
      title: "Last login", key: "last_login",
      render: (_, u) => (
        <Tooltip title={`${fmtWhen(u.last_login)}${u.source === "sso" ? " · last seen in the CA login window" : " · grant time"}`}>
          <Text type="secondary">{relDays(u.last_login)}</Text>
        </Tooltip>
      ),
    },
    { title: "Tier", key: "tier", render: (_, u) => <Text style={{ fontFamily: "var(--font-mono)" }}>{u.tier || "—"}</Text> },
    { title: "Access", key: "access", render: (_, u) => <Tag color={ACCESS_COLOR[u.access]}>{u.access}</Tag> },
    {
      title: "Status", key: "status",
      render: (_, u) => {
        const i = issueByUser.get(u.identity);
        if (!i) return <Text type="secondary">—</Text>;
        const label = i.status === "indeterminate" ? "Unknown" : ISSUE_LABEL[i.kind];
        return (
          <Space size={4}>
            <Badge status={TONE_BADGE[issueTone(i)]} text={label} />
            <HintIcon hint={issueTip(i)} maxWidth={280} />
          </Space>
        );
      },
    },
  ];

  const columns: TableColumnsType<FleetUser> = [
    {
      // Same Avatar + name/email treatment as the Sandboxes table's Owner
      // column — this table only carries a bare `identity` string (no
      // separate display name), so name/email are derived with the same
      // displayFullName/displayEmail helpers FleetPage's adapter uses.
      title: "User", dataIndex: "identity", key: "identity",
      render: (v: string) => (
        <Space size={8} align="center">
          <Avatar size="small" style={{ background: antToken.colorPrimary, fontSize: 11 }}>
            {displayFullName(v).slice(0, 2).toUpperCase()}
          </Avatar>
          <div style={{ minWidth: 0 }}>
            <Text strong style={{ display: "block", fontSize: 13, lineHeight: 1.3 }}>{displayFullName(v)}</Text>
            <Text
              type="secondary"
              style={{ fontSize: 11, fontFamily: "var(--font-mono)", display: "block" }}
              ellipsis={{ tooltip: displayEmail(v) }}
            >
              {displayEmail(v)}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Last active",
      key: "last_active",
      render: (_, u) => (
        <Tooltip title={fmtWhen(u.last_active)}>
          <Text type="secondary">{relDays(u.last_active)}</Text>
        </Tooltip>
      ),
    },
    { title: "Sandboxes", key: "sandboxes", render: (_, u) => u.sandboxes.length },
    { title: "MCP servers", key: "mcp_servers", render: (_, u) => u.mcp_servers.length },
    { title: "Tool calls", dataIndex: "tool_call_count", key: "tool_call_count" },
  ];

  return (
    <Flex vertical gap="middle">
      <PageHeader
        title="Users"
        subtitle="Fleet identities, discovered automatically as people create sandboxes and
          run agents. Expand a user to see their sandboxes and the MCP servers they
          actually use."
      />

      {/* Fleet users — the fleetd registry (learned from SSO logins + SSH-key
          grants), a different source/shape from the sandbox/MCP-activity
          correlation below — not every registered user has driven a sandbox
          or MCP call yet, and vice versa isn't guaranteed either.
          Plain Card, no `title` prop — matches agate's actual Policy
          Management source exactly (thalia/agate/src/pages/policy/ui/
          PolicyOverviewTab.tsx): a bare <CustomCard> with NO title prop (so
          no .ant-card-head divider exists at all), heading rendered as
          ordinary body content inside a vertical Space, fully untouched
          default 24px card padding on every side. The "generous padding"
          agate has is just AntD's own default — not a card-level override,
          and not inflated table-cell padding either (the real Table there
          is size="small", same as here). */}
      <Card>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Title level={2} style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
            {/* span, not Space — Space renders a div, which isn't valid
                phrasing content inside an h2. */}
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              Users
              <HintIcon hint="Includes everyone who has signed in via SSO or been granted SSH-key access." />
            </span>
          </Title>

          {/* Sandbox issues summary. Honest about blindness: when the scan is stale or the
              registry is degraded we say so, so an empty list is never read as "healthy".
              No separate "N users need attention" / "no sandbox issues" states — the table's
              own Status column already shows every user-scoped issue, always in full (this
              table has no pagination), so a redirect-to-below or a healthy-confirmation summary
              would only restate what's already directly visible one scroll away. Host/system
              issues (metaIssues) have no other home, so they still render here — an absence of
              any Alert IS the "no host/system issues" state, inferred rather than announced. */}
          {issues && (blind || metaIssues.length > 0) && (
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              {blind && (
                <Alert
                  type="warning" showIcon
                  message={
                    !issues.registry_ok
                      ? "Sandbox diagnostics degraded — the user registry couldn't be refreshed; issues may be incomplete."
                      : `Sandbox diagnostics degraded — the fleet scan is stale${issues.scan_age_s != null ? ` (${issues.scan_age_s}s old)` : ""}; some conditions can't be confirmed.`
                  }
                />
              )}
              {metaIssues.map((i) => (
                <Alert key={i.id} type={TONE_ALERT[issueTone(i)]} showIcon message={issueLine(i)} />
              ))}
            </Space>
          )}

          {registeredErr && (
            <Alert
              type="error" showIcon
              message={`${registeredErr} — the fleet user registry couldn't load, table stays empty. Try refreshing.`}
            />
          )}
          <Table<RegisteredUser>
            rowKey="identity"
            dataSource={registeredUsers}
            pagination={false}
            size="small"
            locale={{ emptyText: "No fleet users yet — they appear on first SSO login or SSH-key grant." }}
            columns={registeredColumns}
          />
        </Space>
      </Card>

      <Card>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Title level={2} style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Sandbox &amp; MCP activity</Title>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by user, sandbox, or MCP server…"
            prefix={<Search size={14} strokeWidth={1.75} color={antToken.colorTextTertiary} />}
            allowClear
            style={{ maxWidth: 400 }}
          />
          {err && (
            <Alert
              type="error" showIcon
              message={`${err} — sandbox/MCP activity couldn't load, table stays empty. Try refreshing.`}
            />
          )}

          <Table<FleetUser>
          rowKey="identity"
          dataSource={filtered}
          loading={loading}
          pagination={false}
          size="small"
          locale={{ emptyText: q ? "No matching users." : "No users yet." }}
          expandable={{
          expandRowByClick: true,
          expandIcon: ({ expanded, onExpand, record }) => (
            <span
              onClick={(e) => onExpand(record, e)}
              style={{ cursor: "pointer", display: "inline-flex", color: antToken.colorTextTertiary }}
            >
              {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </span>
          ),
          expandedRowRender: (u) => (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 24 }}>
              <div>
                <Flex align="center" gap={6} style={{ marginBottom: 8, fontSize: 12, fontWeight: 500, color: antToken.colorTextSecondary }}>
                  <Boxes size={14} strokeWidth={1.75} /> Sandboxes
                </Flex>
                {u.sandboxes.length === 0 ? (
                  <Text type="secondary">None</Text>
                ) : (
                  <Flex vertical gap={6}>
                    {u.sandboxes.map((s) => (
                      <Flex key={s.id} align="center" gap={8}>
                        <Tag color={STATE_COLOR[s.state]}>{s.state}</Tag>
                        <Text style={{ fontFamily: "var(--font-mono)" }}>{s.username}</Text>
                        {s.public_ip && <Text type="secondary">{s.public_ip}</Text>}
                        <Text type="secondary">· started {relDays(s.last_started_at ?? "")}</Text>
                      </Flex>
                    ))}
                  </Flex>
                )}
              </div>
              <div>
                <Flex align="center" gap={6} style={{ marginBottom: 8, fontSize: 12, fontWeight: 500, color: antToken.colorTextSecondary }}>
                  <Server size={14} strokeWidth={1.75} /> MCP servers used
                </Flex>
                {u.mcp_servers.length === 0 ? (
                  <Text type="secondary">None</Text>
                ) : (
                  <Flex vertical gap={6}>
                    {u.mcp_servers.map((s) => (
                      <Flex key={s.server} align="center" gap={8}>
                        <Text style={{ fontFamily: "var(--font-mono)" }}>{s.server}</Text>
                        <Text type="secondary">{s.tool_count} call{s.tool_count === 1 ? "" : "s"}</Text>
                        <Text type="secondary">· {relDays(s.last_used)}</Text>
                      </Flex>
                    ))}
                  </Flex>
                )}
              </div>
            </div>
          ),
          }}
          columns={columns}
          />
        </Space>
      </Card>
    </Flex>
  );
}
